import { useEffect, useState } from "react";
import EventEmitter from "eventemitter3";
import PQueue from "p-queue";
import isEqual from "react-fast-compare";
import {
  QMFile,
  FileUpload,
  MinimalFile,
  QueuedFile,
  UploadFile,
  PersistedFileFromServer,
  MediaFile,
  SavedFile,
  PersistableFile,
} from "./MediaSliderTypes";
import { useStateWithRef } from "@app/components/questkit/useStateWithRef";
import { Platform } from "react-native";
import { MediaItemType } from "@questmate/common/dist/types/MediaItem";
import { uuid } from "@app/util/uuid";
import {
  waitForAssembly,
  TransloadItAssemblyOptions,
  createAssemblyAndUpload,
} from "./transloaditClient";

const uploadFilesQueue = new PQueue({ concurrency: 5 });

// export const MAX_FILE_COUNT = 64; // Arbitrary limit for now
// export const MAX_FILE_SIZE = 500 * 1024 ** 2; // 500 MB

export interface MediaSliderStateEvents {
  "files:update": (files: MinimalFile[]) => void;
  "files:update:detailed": (files: QMFile[]) => void;
  "files:error": (error: Error) => void;
  "files:selection:changed": (selectedFileId: string | null) => void;
}

export type MediaSliderStateConstructorParams = {
  initialFiles?: PersistedFileFromServer[];
  getAssemblyOptions?: () => Promise<TransloadItAssemblyOptions>;
};

export class MediaSliderState extends EventEmitter<MediaSliderStateEvents> {
  private _getAssemblyOptions: MediaSliderStateConstructorParams["getAssemblyOptions"];
  private _files: QMFile[] = [];
  private _lastMinimalFileUpdate: MinimalFile[] = [];
  private _selectedFileId: string | null = null;
  private _isDestroyed: boolean = false;

  constructor(params: MediaSliderStateConstructorParams) {
    super();
    const { initialFiles = [], getAssemblyOptions } = params;

    // Store assembly options for later use
    this._getAssemblyOptions = getAssemblyOptions;

    // Initialize files from files
    this._files = initialFiles.map((file) => {
      return {
        state: "SAVED" as const,
        id: uuid(),
        src: file.signedPreviewUrl,
        name: file.originalBasename,
        ...file,
      };
    });
    this._lastMinimalFileUpdate = MediaSliderState.getMinimalFileData(
      this._files
    );

    // Initialize selected file with the first file if available
    if (this._files.length > 0) {
      this._selectedFileId = this._files[0].id;
    }
  }

  public uploadFiles(
    files: {
      localPreviewUri?: string;
      file: MediaFile;
    }[]
  ) {
    this.throwIfDestroyed();
    const newFiles = files.map(({ localPreviewUri, file }) => {
      const lastDotIndex = file.name.lastIndexOf(".");
      const basename = file.name.substring(0, lastDotIndex);
      return {
        state: "QUEUED" as const,
        id: uuid(),
        src: localPreviewUri ?? "",
        name: basename,
        type: file.type?.startsWith("image")
          ? MediaItemType.Image
          : file.type?.startsWith("video")
          ? MediaItemType.Video
          : MediaItemType.Raw,
        file,
      } satisfies QueuedFile;
    });

    this.updateFiles([...this._files, ...newFiles]);

    // If there are new files and no file is currently selected, select the first new file
    if (newFiles.length > 0 && this._selectedFileId === null) {
      this.setSelectedFileId(newFiles[0].id);
    }

    // Queue each file for upload
    for (const queuedFile of newFiles) {
      void uploadFilesQueue.add(() => this.internalUpload(queuedFile));
    }

    return newFiles;
  }

  private async internalUpload(file: QueuedFile) {
    if (!this.isUploadingEnabled()) {
      throw new Error("No assembly options configured");
    }
    this.updateFilesWhere(
      (f) => f.id === file.id,
      (f) =>
        ({
          ...(f as QueuedFile),
          state: "PREPARING" as const,
        } satisfies FileUpload.Preparing)
    );

    try {
      const options = await this._getAssemblyOptions!();
      const fileUploadResponse = await createAssemblyAndUpload(
        options,
        file.file
      );

      const assemblyId = fileUploadResponse.assembly_id;
      this.updateFilesWhere(
        (f) => f.id === file.id,
        (f) =>
          ({
            ...f,
            state: "UPLOADING" as const,
            assemblyId,
            uploadProgress: 0,
          } as FileUpload.Uploading)
      );

      const finished = await waitForAssembly(assemblyId);
      if (finished.ok) {
        this.updateFilesWhere(
          (f) => f.id === file.id,
          (f) => {
            const uploadingFile = f as FileUpload.Uploading;
            return {
              ...uploadingFile,
              state: "READY_TO_SAVE" as const,
              identifier: finished.result.uploads[0].original_id,
              originalBasename: Platform.select({
                // Web file name comes back correct from TransloadIt.
                web: finished.result.uploads[0].original_basename,
                // Native file name is gibberish, so use the one we first set.
                default: uploadingFile.name,
              }),
              extension: finished.result.uploads[0].ext,
              mime: finished.result.uploads[0].mime,
              uploadProgress: 100,
            } satisfies FileUpload.Ready;
          }
        );
      } else {
        this.updateFilesWhere(
          (f) => f.id === file.id,
          (f) =>
            ({
              ...(f as FileUpload.Uploading),
              state: "ERROR" as const,
              message: "Upload failed",
              errorData: {
                error: {
                  name: "AssemblyError",
                  message: "Assembly processing failed",
                  details: finished.error,
                },
              },
            } satisfies FileUpload.Error)
        );
      }
    } catch (error) {
      console.error("Upload failed:", error);
      this.updateFilesWhere(
        (f) => f.id === file.id,
        (f) =>
          ({
            ...(f as FileUpload.Preparing | FileUpload.Uploading),
            state: "ERROR" as const,
            message: "Upload failed",
            errorData: {
              error: {
                name: error instanceof Error ? error.name : "Error",
                message: error instanceof Error ? error.message : String(error),
              },
            },
          } satisfies FileUpload.Error)
      );
    }
  }

  public removeFile(fileId: string) {
    this.throwIfDestroyed();
    const fileToRemove = this._files.find((file) => file.id === fileId);

    if (!fileToRemove) {
      return false;
    }

    // If the removed file is the selected one, select the next file or the previous one if it was the last
    if (this._selectedFileId === fileId) {
      const currentIndex = this._files.findIndex((img) => img.id === fileId);
      if (currentIndex !== -1) {
        // Select next file or previous if this is the last one
        const nextFileId =
          this._files[
            currentIndex === this._files.length - 1
              ? currentIndex - 1
              : currentIndex + 1
          ]?.id ?? null;
        this.setSelectedFileId(nextFileId);
      }
    }

    this.updateFiles(this._files.filter((file) => file.id !== fileId));

    return true;
  }

  /**
   * Retry uploading a failed file
   */
  public retryUpload(fileId: string) {
    this.throwIfDestroyed();
    const fileToRetry = this._files.find((file) => file.id === fileId);

    if (!fileToRetry || fileToRetry.state !== "ERROR") {
      return false;
    }

    // Reset the file to QUEUED state and retry upload
    this.updateFilesWhere(
      ({ id }) => id === fileId,
      (_file) => {
        const file = _file as UploadFile;
        return {
          state: "QUEUED" as const,
          id: file.id,
          src: file.src,
          name: file.name,
          type: file.type,
          file: file.file,
        } as QueuedFile;
      }
    );

    const queuedFile = this.getFile(fileId)! as QueuedFile;
    void uploadFilesQueue.add(() => this.internalUpload(queuedFile));

    return true;
  }

  public reorderFiles(fromIndex: number, toIndex: number) {
    this.throwIfDestroyed();
    if (fromIndex === toIndex) {
      return;
    }

    const newFiles = [...this._files];
    const [movedFile] = newFiles.splice(fromIndex, 1);
    newFiles.splice(toIndex, 0, movedFile);

    this.updateFiles(newFiles);
  }

  public get files(): QMFile[] {
    this.throwIfDestroyed();
    return [...this._files];
  }

  public getFile(fileId: string): QMFile | undefined {
    this.throwIfDestroyed();
    return this._files.find((file) => file.id === fileId);
  }

  public get selectedFileId(): string | null {
    this.throwIfDestroyed();
    return this._selectedFileId;
  }

  public setSelectedFileId(fileId: string): void {
    this.throwIfDestroyed();
    if (fileId !== null && !this._files.some(({ id }) => id === fileId)) {
      throw new Error(
        `Cannot set selected file id to '${fileId}' as no file exists with that ID.`
      );
    }

    if (this._selectedFileId !== fileId) {
      this._selectedFileId = fileId;
      this.emit("files:selection:changed", fileId);
    }
  }

  public setSelectedFileIndex(fileIndex: number): void {
    this.throwIfDestroyed();
    if (fileIndex < 0 || fileIndex >= this._files.length) {
      throw new Error(
        `Cannot set selected file index to '${fileIndex}' as no file exists with that index.`
      );
    }

    const newSelectedFileId = this._files[fileIndex].id;
    if (this._selectedFileId !== newSelectedFileId) {
      this.setSelectedFileId(newSelectedFileId);
    }
  }

  public selectPreviousFile(): void {
    this.throwIfDestroyed();
    if (this._files.length < 2) {
      return;
    }

    const selectedIndex = this._files.findIndex(
      ({ id }) => id === this._selectedFileId
    );
    if (selectedIndex === 0) {
      this.setSelectedFileId(this._files[this._files.length - 1].id);
    } else {
      this.setSelectedFileId(this._files[selectedIndex - 1].id);
    }
  }

  public selectNextFile(): void {
    this.throwIfDestroyed();
    if (this._files.length < 2) {
      return;
    }

    const selectedIndex = this._files.findIndex(
      ({ id }) => id === this._selectedFileId
    );
    if (selectedIndex === this._files.length - 1) {
      this.setSelectedFileId(this._files[0].id);
    } else {
      this.setSelectedFileId(this._files[selectedIndex + 1].id);
    }
  }

  public updateSavedFiles(
    latestServerFiles: readonly (PersistedFileFromServer | PersistableFile)[]
  ) {
    this.updateFiles(
      this._files.map((_file) => {
        if ("identifier" in _file) {
          const file = _file as FileUpload.Ready | SavedFile;
          const matchingServerFile = latestServerFiles.find(
            ({ identifier }) => file.identifier === identifier
          );

          // The `signedPreviewUrl` is the only indicator that we have a saved file from the server.
          if (
            matchingServerFile &&
            "signedPreviewUrl" in matchingServerFile &&
            matchingServerFile.signedPreviewUrl
          ) {
            return {
              ...matchingServerFile,
              id: file.id,
              name: file.originalBasename,
              state: "SAVED" as const,
              src: matchingServerFile.signedPreviewUrl,
            };
          }
        }
        return _file;
      })
    );
  }

  public destroy() {
    this.removeAllListeners();
    this._getAssemblyOptions = undefined;
    this._files = [];
    this._lastMinimalFileUpdate = [];
    this._selectedFileId = null;
    this._isDestroyed = true;
  }

  /**
   * Identify files to update.
   * Return updated files from updater or `null` to remove the file.
   */
  private updateFilesWhere(
    condition: (file: QMFile) => boolean,
    updater: (file: QMFile) => QMFile | null
  ) {
    this.updateFiles(
      this._files.flatMap((file) => {
        const result = condition(file) ? updater(file) : file;
        if (result === null) {
          return [];
        } else {
          return [result];
        }
      })
    );
  }

  private updateFiles(newFiles: QMFile[]) {
    this._files = newFiles;
    this.emit("files:update:detailed", this._files);
    const minimalFileData = MediaSliderState.getMinimalFileData(this._files);
    if (!isEqual(minimalFileData, this._lastMinimalFileUpdate)) {
      this._lastMinimalFileUpdate = minimalFileData;
      this.emit("files:update", minimalFileData);
    }
  }

  public isUploadingEnabled(): boolean {
    return this._getAssemblyOptions !== undefined;
  }

  private throwIfDestroyed() {
    if (this._isDestroyed) {
      throw new Error(
        "Requested an action on an MediaSliderState that is destroyed!"
      );
    }
  }

  public static getMinimalFileData(files: QMFile[]): MinimalFile[] {
    return files.map((file) => {
      if (file.state === "SAVED") {
        return {
          state: file.state,
          identifier: file.identifier,
          name: file.name,
          originalBasename: file.originalBasename,
          extension: file.extension,
          mime: file.mime,
          type: file.type,
          signedUrl: file.signedUrl,
          signedPreviewUrl: file.signedPreviewUrl,
        };
      } else if (file.state === "READY_TO_SAVE") {
        return {
          state: file.state,
          identifier: file.identifier,
          originalBasename: file.originalBasename,
          extension: file.extension,
          mime: file.mime,
          type: file.type,
        };
      } else if (file.state === "QUEUED") {
        return {
          state: file.state,
          name: file.name,
        };
      } else {
        return {
          state: file.state,
        };
      }
    });
  }
}

export const useFileIds = (state: MediaSliderState) => {
  const [fileIds, setFileIds, fileIdsRef] = useStateWithRef<string[]>(() =>
    state.files.map(({ id }) => id)
  );

  useEffect(() => {
    const listener = (updatedFiles: QMFile[]) => {
      const updatedFileIds = updatedFiles.map(({ id }) => id);
      if (!isEqual(updatedFiles, fileIdsRef.current)) {
        setFileIds(updatedFileIds);
      }
    };

    // Explicit update to handle when the state is replaced
    listener(state.files);

    state.on("files:update:detailed", listener);

    return () => {
      state.off("files:update:detailed", listener);
    };
  }, [fileIdsRef, setFileIds, state]);

  return fileIds;
};

export const useFile = (state: MediaSliderState, fileId: string) => {
  const [file, setFile] = useState<QMFile | undefined>(() =>
    state.getFile(fileId)
  );

  useEffect(() => {
    const listener = () => {
      setFile(state.getFile(fileId));
    };

    // Explicit update to handle when the state is replaced
    listener();

    state.on("files:update:detailed", listener);
    return () => {
      state.off("files:update:detailed", listener);
    };
  }, [state, fileId]);

  return file;
};
